/**
 * LossCurve — an SFT run's loss per optimizer step in the RewardCurve
 * dialect: training loss as an ink line, held-out evaluation loss as a
 * dashed line through the steps it was measured at, and the latest
 * training step marked so the live edge of the run is findable.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { TrainingLossSeries } from '@/api/generated/model'
import { CHART_INK } from '@/components/evals/chartStyle'
import { type LossPoint, lossPoints } from '@/components/training/loss'

const LEGEND = [
  {
    mark: <span className="h-[2px] w-4" style={{ backgroundColor: CHART_INK }} />,
    label: 'Training loss',
  },
  {
    mark: <span className="w-4 border-t border-dashed border-foreground" />,
    label: 'Evaluation loss',
  },
]

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }

function LossTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
}): React.JSX.Element | null {
  const point = payload?.[0]?.payload as LossPoint | undefined
  if (!active || !point) return null
  return (
    <div className="border border-border bg-card p-2.5 text-xs">
      <p className="font-mono uppercase tracking-wider text-muted-foreground">
        Step {point.step.toLocaleString()}
      </p>
      {point.loss !== null && (
        <p className="mt-1">
          Training loss: <span className="font-mono tabular-nums">{point.loss.toFixed(3)}</span>
        </p>
      )}
      {point.evalLoss !== null && (
        <p className="mt-1">
          Evaluation loss:{' '}
          <span className="font-mono tabular-nums">{point.evalLoss.toFixed(3)}</span>
        </p>
      )}
    </div>
  )
}

function Legend(): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {LEGEND.map((entry) => (
        <span
          key={entry.label}
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
        >
          {entry.mark}
          {entry.label}
        </span>
      ))}
    </div>
  )
}

export function LossCurve({
  loss,
  evalLoss,
  totalSteps,
}: {
  loss: TrainingLossSeries | null
  evalLoss: TrainingLossSeries | null
  totalSteps: number
}): React.JSX.Element {
  const points = lossPoints(loss, evalLoss)
  if (points.length === 0) {
    return <p className="text-sm text-muted-foreground">No loss has been measured yet.</p>
  }
  const latestStep = loss?.steps[loss.steps.length - 1]
  return (
    <div className="space-y-4">
      <Legend />
      <div
        className="h-[320px] w-full"
        role="img"
        aria-label="Training and evaluation loss by optimizer step"
      >
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <LineChart data={points} margin={{ top: 24, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke="hsl(var(--border) / 0.6)" />
            <XAxis
              dataKey="step"
              type="number"
              domain={[0, totalSteps]}
              tickLine={false}
              axisLine={false}
              tick={AXIS_TICK}
              tickFormatter={(value: number) => value.toLocaleString()}
            />
            <YAxis
              domain={[0, 'auto']}
              tickLine={false}
              axisLine={false}
              width={44}
              tick={AXIS_TICK}
              tickFormatter={(value: number) => value.toFixed(2)}
            />
            <Tooltip
              content={({ active, payload }) => <LossTooltip active={active} payload={payload} />}
              cursor={{ stroke: 'hsl(var(--muted-foreground) / 0.4)', strokeDasharray: '3 3' }}
            />
            <Line
              dataKey="loss"
              stroke={CHART_INK}
              strokeWidth={1.5}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            <Line
              dataKey="evalLoss"
              stroke="hsl(var(--foreground))"
              strokeWidth={1}
              strokeDasharray="4 3"
              dot={{ r: 3 }}
              connectNulls
              isAnimationActive={false}
            />
            {loss !== null && latestStep !== undefined && (
              <ReferenceDot
                x={latestStep}
                y={loss.value}
                r={3.5}
                fill="hsl(var(--foreground))"
                stroke="none"
                // Below the point and ending at it: the latest step sits on
                // the right edge, and evaluation loss usually runs above it.
                label={{
                  value: `Step ${latestStep.toLocaleString()} · ${loss.value.toFixed(3)}`,
                  position: 'bottom',
                  textAnchor: 'end',
                  fontSize: 10,
                  fill: 'hsl(var(--foreground))',
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
